import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AdminInfo } from "@/types/superAdmin";
import { Building2, Plus, X } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTranslation } from "react-i18next";

interface ManageTenantsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  admin: AdminInfo | null;
  onAssign: (userId: string, gymId: string) => void;
  onRevoke: (userId: string, gymId: string) => void;
}

export function ManageTenantsDialog({ open, onOpenChange, admin, onAssign, onRevoke }: ManageTenantsDialogProps) {
  const { t } = useTranslation(["superadmin", "common"]);
  const [selectedGymId, setSelectedGymId] = useState<string>("");
  const isMobile = useIsMobile();

  const { data: allGyms = [] } = useQuery({
    queryKey: ['all-gyms'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('gyms')
        .select('id, name, city')
        .neq('id', '00000000-0000-0000-0000-000000000000')
        .order('name');
      if (error) throw error;
      return data;
    },
  });

  if (!admin) return null;

  const assignedGymIds = admin.tenants.map(t => t.gym_id);
  const availableGyms = allGyms.filter(gym => !assignedGymIds.includes(gym.id));

  const handleAssign = () => {
    if (selectedGymId && admin) {
      onAssign(admin.user_id, selectedGymId);
      setSelectedGymId("");
    }
  };

  const content = (
    <div className="space-y-6">
      {/* Tenants actuels */}
      <div>
        <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
          <Building2 className="h-4 w-4" />
          {t("manageTenants.currentTenants")} ({admin.tenants.length})
        </h3>
        <div className="space-y-2">
          {admin.tenants.map((tenant) => (
            <div
              key={tenant.gym_id}
              className="flex items-center justify-between p-3 border rounded-lg bg-card"
            >
              <div>
                <p className="font-medium text-sm md:text-base">{tenant.gym_name}</p>
                <p className="text-xs md:text-sm text-muted-foreground">{tenant.city}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onRevoke(admin.user_id, tenant.gym_id)}
                disabled={admin.tenants.length === 1}
                className="text-destructive hover:text-destructive hover:bg-destructive/10"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* Ajouter un tenant */}
      {availableGyms.length > 0 && (
        <div>
          <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
            <Plus className="h-4 w-4" />
            {t("manageTenants.assignNewTenant")}
          </h3>
          <div className="flex flex-col md:flex-row gap-2">
            <Select value={selectedGymId} onValueChange={setSelectedGymId}>
              <SelectTrigger className="flex-1">
                <SelectValue placeholder={t("manageTenants.selectTenantPlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                {availableGyms.map((gym) => (
                  <SelectItem key={gym.id} value={gym.id}>
                    {gym.name} - {gym.city}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={handleAssign} disabled={!selectedGymId} className="w-full md:w-auto">
              {t("manageTenants.add")}
            </Button>
          </div>
        </div>
      )}

      {availableGyms.length === 0 && admin.tenants.length > 0 && (
        <div className="text-center py-4 text-sm text-muted-foreground">
          {t("manageTenants.alreadyAllAssigned")}
        </div>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>
              {t("manageTenants.title")} - {admin.first_name} {admin.last_name}
            </DrawerTitle>
          </DrawerHeader>
          <div className="px-4 pb-4 overflow-y-auto max-h-[70vh]">
            {content}
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {t("manageTenants.title")} - {admin.first_name} {admin.last_name}
          </DialogTitle>
        </DialogHeader>
        {content}
      </DialogContent>
    </Dialog>
  );
}
